import { ref } from 'vue';
import JsSIP from 'jssip';

const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_WS_PORT = 8088;
const DEFAULT_WSS_PORT = 8089;

const buildWebSocketUrl = ({ sipServer, sipPort, sipTransport }) => {
  const protocol = sipTransport === 'wss' ? 'wss://' : 'ws://';
  const port = parseInt(sipPort, 10);
  const fallbackPort = sipTransport === 'wss' ? DEFAULT_WSS_PORT : DEFAULT_WS_PORT;
  const normalizedPort = Number.isNaN(port) ? fallbackPort : port;

  return `${protocol}${sipServer}:${normalizedPort}`;
};

const normalizeConnectionError = error => {
  if (error.message.includes('timeout')) {
    return 'Connection timeout. Please check server address and port.';
  }

  if (error.message.includes('refused')) {
    return 'Connection refused. Server may be offline or port is incorrect.';
  }

  if (error.message.includes('ENOTFOUND')) {
    return 'Server not found. Please check the hostname.';
  }

  return `Connection failed: ${error.message}`;
};

const normalizeRegistrationFailure = cause => {
  const normalizedCause = String(cause || '').toLowerCase();

  if (
    normalizedCause.includes('authentication') ||
    normalizedCause.includes('forbidden') ||
    normalizedCause.includes('unauthorized') ||
    normalizedCause.includes('rejected')
  ) {
    return 'Registration rejected by the SIP server. Check extension and password.';
  }

  if (normalizedCause.includes('timeout')) {
    return 'Registration timeout. The SIP server did not confirm this extension in time.';
  }

  if (
    normalizedCause.includes('transport') ||
    normalizedCause.includes('connection')
  ) {
    return 'Could not reach the SIP transport for this extension validation.';
  }

  return cause
    ? `Registration failed: ${cause}`
    : 'Registration failed. Check the SIP credentials and server settings.';
};

export function useSipValidation() {
  const isValidating = ref(false);
  const validationStatus = ref(null); // 'success', 'error', null
  const validationMessage = ref('');
  const lastValidationTime = ref(null);

  async function validateSipConnection({ sipServer, sipPort, sipTransport }) {
    console.log('[useSipValidation] Called with:', {
      sipServer,
      sipPort,
      sipTransport,
    });

    if (!sipServer || !sipTransport) {
      validationStatus.value = null;
      validationMessage.value = '';
      return false;
    }

    // Força limpeza do estado anterior
    isValidating.value = false;
    validationStatus.value = null;
    validationMessage.value = '';

    // Aguarda um tick para limpar visualmente
    await new Promise(resolve => {
      setTimeout(resolve, 50);
    });

    isValidating.value = true;
    validationStatus.value = null;
    validationMessage.value = 'Testing connection...';

    try {
      // Construir WebSocket URL
      const wsUrl = buildWebSocketUrl({
        sipServer,
        sipPort,
        sipTransport,
      });

      console.log('[useSipValidation] Testing connection to:', wsUrl);

      // Criar socket de teste direto (sem JsSIP UA)
      const socket = new JsSIP.WebSocketInterface(wsUrl);

      // Timeout de 5 segundos
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          socket.disconnect();
          reject(new Error('Connection timeout'));
        }, 5000);
      });

      // Promessa de conexão - aguarda apenas WebSocket conectar
      const connectionPromise = new Promise((resolve, reject) => {
        // Sucesso: WebSocket conectou
        socket.onconnect = () => {
          console.log('[useSipValidation] ✓ WebSocket connected successfully');
          socket.disconnect();
          resolve(true);
        };

        // Erro: WebSocket falhou
        socket.ondisconnect = error => {
          if (error) {
            const errorMsg = error.reason || error.code || 'Connection refused';
            console.error('[useSipValidation] ✗ WebSocket error:', errorMsg);
            reject(new Error(`WebSocket error: ${errorMsg}`));
          }
        };

        // Conectar (isso dispara onconnect quando bem-sucedido)
        socket.connect();
      });

      // Aguardar conexão ou timeout
      await Promise.race([connectionPromise, timeoutPromise]);

      validationStatus.value = 'success';
      validationMessage.value =
        'Connection successful! SIP server is reachable.';
      lastValidationTime.value = new Date();
      return true;
    } catch (error) {
      validationStatus.value = 'error';
      lastValidationTime.value = new Date();

      // Mensagens de erro mais amigáveis
      validationMessage.value = normalizeConnectionError(error);

      return false;
    } finally {
      isValidating.value = false;
    }
  }

  return {
    isValidating,
    validationStatus,
    validationMessage,
    lastValidationTime,
    validateSipConnection,
  };
}

export async function validateSipCredentials({
  sipServer,
  sipPort,
  sipTransport,
  sipRealm,
  extension,
  password,
  displayName,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) {
  if (!sipServer || !sipTransport || !extension || !password) {
    return {
      ok: false,
      message:
        'SIP server, transport, extension, and password are required for validation.',
    };
  }

  const wsUrl = buildWebSocketUrl({
    sipServer,
    sipPort,
    sipTransport,
  });
  const realm = sipRealm || sipServer;

  return new Promise(resolve => {
    let timeoutId = null;
    let ua = null;
    let settled = false;

    const finalize = result => {
      if (settled) {
        return;
      }

      settled = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      try {
        ua?.stop();
      } catch (cleanupError) {
        console.warn(
          '[useSipValidation] Failed to stop validation UA:',
          cleanupError
        );
      }

      resolve(result);
    };

    try {
      const socket = new JsSIP.WebSocketInterface(wsUrl);

      ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${extension}@${realm}`,
        password,
        display_name: displayName || extension,
        session_timers: false,
        register_expires: 60,
      });

      timeoutId = setTimeout(() => {
        finalize({
          ok: false,
          message:
            'Validation timeout. The SIP server did not confirm the extension in time.',
        });
      }, timeoutMs);

      ua.on('registered', () => {
        finalize({
          ok: true,
          message:
            'Extension registered successfully. Credentials look valid for this SIP inbox.',
        });
      });

      ua.on('registrationFailed', event => {
        finalize({
          ok: false,
          message: normalizeRegistrationFailure(event?.cause),
        });
      });

      ua.on('disconnected', event => {
        if (settled) {
          return;
        }

        finalize({
          ok: false,
          message: normalizeRegistrationFailure(
            event?.error || event?.cause || 'Connection closed'
          ),
        });
      });

      ua.start();
    } catch (error) {
      finalize({
        ok: false,
        message: normalizeConnectionError(error),
      });
    }
  });
}
